import React, { useEffect } from 'react';
import { Palette, Globe, Bell, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { usePrefs } from '../hooks/usePrefs';
import { themeOptions, languageOptions } from '../constants';

function SectionCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center gap-3 px-5 pb-3 pt-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
          {icon}
        </div>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  name,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  name: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <label htmlFor={name} className="flex-1 cursor-pointer">
        <p className={`text-sm font-medium ${disabled ? 'text-slate-400' : 'text-slate-900'}`}>{label}</p>
        {description && <p className="text-xs text-slate-500">{description}</p>}
      </label>
      <label className="relative ml-3 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center">
        <input
          id={name}
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
        <span className="block h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-slate-900 peer-disabled:opacity-40" />
        <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
      </label>
    </div>
  );
}

const PrefsPage: React.FC = () => {
  useEffect(() => { document.title = 'Preferências'; }, []);
  const { prefs, handleChange, playNotificationSound } = usePrefs();

  return (
    <main className="space-y-4 px-4 py-6" id="main-content" role="main" aria-label="Preferências">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Preferências</h1>
        <p className="mt-1 text-sm text-slate-500">Personalize a sua experiência no app.</p>
      </div>

      <SectionCard title="Aparência" icon={<Palette className="h-4 w-4" />}>
        <label className="mb-1.5 block text-xs font-medium text-slate-500" htmlFor="theme-select">
          Tema
        </label>
        <select
          id="theme-select"
          value={prefs.theme}
          onChange={handleChange('theme')}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
        >
          {themeOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </SectionCard>

      <SectionCard title="Idioma" icon={<Globe className="h-4 w-4" />}>
        <label className="mb-1.5 block text-xs font-medium text-slate-500" htmlFor="language-select">
          Idioma do aplicativo
        </label>
        <select
          id="language-select"
          value={prefs.language}
          onChange={handleChange('language')}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
        >
          {languageOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </SectionCard>

      <SectionCard title="Notificações" icon={<Bell className="h-4 w-4" />}>
        <div className="space-y-3">
          <ToggleRow
            name="notifications-enabled"
            label="Ativar notificações"
            description="Permite receber lembretes do app"
            checked={!!prefs.notificationsEnabled}
            onChange={handleChange('notificationsEnabled')}
          />
          <Separator />
          <ToggleRow
            name="notifications-beep"
            label="Som de bipe"
            description="Reproduz um bipe ao receber notificações"
            checked={!!prefs.notificationsBeep}
            onChange={handleChange('notificationsBeep')}
            disabled={!prefs.notificationsEnabled}
          />
          <Separator />
          <div className={!prefs.notificationsEnabled ? 'opacity-40 pointer-events-none' : ''}>
            <label className="mb-1.5 block text-xs font-medium text-slate-500" htmlFor="sound-url">
              Som personalizado (URL ou nome do arquivo)
            </label>
            <input
              id="sound-url"
              type="text"
              value={prefs.notificationsSound}
              onChange={handleChange('notificationsSound')}
              disabled={!prefs.notificationsEnabled}
              placeholder="https://exemplo.com/som.mp3"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-400 focus:ring-1 focus:ring-slate-400 disabled:opacity-40"
            />
          </div>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={playNotificationSound}
              disabled={!prefs.notificationsEnabled}
              className="gap-2"
            >
              <Volume2 className="h-4 w-4" aria-hidden="true" />
              Testar notificação
            </Button>
          </div>
        </div>
      </SectionCard>
    </main>
  );
};

export default PrefsPage;
